import {
  paymentMethods,
  qualifiesForFreeShipping,
} from "../../utils/global_const";
import CreditCardsCheckout from "../../components/stripe/CreditCardsCheckout";
import { useContext, useEffect, useState } from "react";
import CreditCardButton from "../stripe/CreditCardButton";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ProductContext } from "../../context/ProductContext";
import styled from "styled-components";
import {
  Card,
  CardTitle,
  RadioCard,
  RadioCircle,
  RadioContent,
  RadioTitle,
  InfoBanner,
  InfoBannerLabel,
  InfoBannerText,
} from "./CheckoutUI";

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--bg-100);
  color: var(--primary-100);
`;
const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;
const ReviewStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  width: 100%;
`;

const AddressPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 9.5C5 6.09371 8.00993 3 12 3C15.9901 3 19 6.09371 19 9.5C19 11.6449 17.6877 14.0406 15.9606 16.2611C14.5957 18.016 13.0773 19.5329 12 20.5944C10.9227 19.5329 9.40427 18.016 8.03935 16.2611C6.31229 14.0406 5 11.6449 5 9.5ZM12 1C6.99007 1 3 4.90629 3 9.5C3 12.3551 4.68771 15.2094 6.46065 17.4889C7.99487 19.4615 9.7194 21.1574 10.7973 22.2173C10.9831 22.4001 11.1498 22.564 11.2929 22.7071C11.4804 22.8946 11.7348 23 12 23C12.2652 23 12.5196 22.8946 12.7071 22.7071C12.8502 22.564 13.0169 22.4001 13.2027 22.2174L13.2028 22.2173C14.2806 21.1573 16.0051 19.4615 17.5394 17.4889C19.3123 15.2094 21 12.3551 21 9.5C21 4.90629 17.0099 1 12 1ZM12 12.5C13.3807 12.5 14.5 11.3807 14.5 10C14.5 8.61929 13.3807 7.5 12 7.5C10.6193 7.5 9.5 8.61929 9.5 10C9.5 11.3807 10.6193 12.5 12 12.5Z"
      fill="currentColor"
    />
  </svg>
);

const TruckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 7H13V16H3V7Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M13 10H17L20 13V16H13V10Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="6" cy="18" r="1.6" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="17" cy="18" r="1.6" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const Step3 = ({ setPaymentMethod, address, shippingInfo, onSubmit }) => {
  const { t } = useTranslation();
  const { currencyTag } = useContext(ProductContext);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentMethodLocal, setPaymentMethodLocal] = useState("cod");
  const cartItems = useSelector((state) => state.cart.itemsList);
  const subtotalRounded = parseFloat(
    cartItems
      .reduce(
        (total, item) => total + item.product.discount_price * item.quantity,
        0
      )
      .toFixed(2)
  );
  const shippingCost =
    shippingInfo?.name &&
    qualifiesForFreeShipping(subtotalRounded, shippingInfo.name)
      ? 0
      : shippingInfo?.price ?? 0;

  useEffect(() => {
    setPaymentMethod(paymentMethodLocal);
  }, [paymentMethodLocal, setPaymentMethod]);

  const selectedMethod = paymentMethods.find(
    (method) => method.code === paymentMethodLocal
  );

  return (
    <>
      <ReviewStack>
        {address && (
          <InfoBanner>
            <InfoRow>
              <InfoIcon>
                <AddressPinIcon />
              </InfoIcon>
              <div>
                <InfoBannerLabel>{t("CHECKOUT.DELIVER_TO")}</InfoBannerLabel>
                <InfoBannerText>
                  {`${address.street} ${address.street_number}, ${address.city} ${address.postal_code}, ${address.country}`}
                </InfoBannerText>
              </div>
            </InfoRow>
          </InfoBanner>
        )}
        {shippingInfo?.name && (
          <InfoBanner>
            <InfoRow>
              <InfoIcon>
                <TruckIcon />
              </InfoIcon>
              <div>
                <InfoBannerLabel>
                  {t("CHECKOUT.DELIVERY_OPTIONS")}
                </InfoBannerLabel>
                <InfoBannerText>
                  {shippingInfo.name} —{" "}
                  {shippingCost === 0
                    ? t("CHECKOUT.FREE")
                    : `${currencyTag}${shippingCost.toFixed(2)}`}
                </InfoBannerText>
              </div>
            </InfoRow>
          </InfoBanner>
        )}
      </ReviewStack>

      <Card>
        <CardTitle>{t("PAYMENT_METHOD.SELECT")}</CardTitle>
        {paymentMethods.map((method) => {
          const isSelected = paymentMethodLocal === method.code;
          return (
            <RadioCard
              key={method.code}
              $isSelected={isSelected}
              $disabled={!method.available}
            >
              <input
                type="radio"
                value={method.code}
                checked={isSelected}
                onChange={() => setPaymentMethodLocal(method.code)}
                disabled={!method.available}
                style={{ display: "none" }}
              />
              <RadioCircle $isSelected={isSelected} />
              <RadioContent>
                <RadioTitle>
                  {t(`PAYMENT_METHOD.${method.code.toUpperCase()}`)}
                  {!method.available &&
                    ` ${t("PAYMENT_METHOD.UNAVAILABLE")}`}
                </RadioTitle>
              </RadioContent>
            </RadioCard>
          );
        })}
      </Card>

      {selectedMethod?.code === "card" && selectedMethod.available && (
        <>
          <CreditCardsCheckout
            address={address}
            setClientSecret={setClientSecret}
          />
          <CreditCardButton
            clientSecret={clientSecret}
            handleOrderSubmission={onSubmit}
          />
        </>
      )}
    </>
  );
};

export default Step3;
